import { useRef, useEffect } from 'react';
import { Renderer, Program, Triangle, Mesh } from 'ogl';
import './RippleGrid.css';

const RippleGrid = ({
  enableRainbow = false,
  gridColor = '#ffffff',
  rippleIntensity = 0.05,
  rippleSpeed = 0.5,
  gridSize = 4.0,
  gridThickness = 15.0,
  fadeDistance = 1.5,
  vignetteStrength = 2.0,
  glowIntensity = 0.1,
  opacity = 1.0,
  gridRotation = 0,
  mouseInteraction = true,
  mouseInteractionRadius = 1,
  isTyping = false
}) => {
  const containerRef = useRef(null);
  const mousePositionRef = useRef({ x: 0.5, y: 0.5 });
  const targetMouseRef = useRef({ x: 0.5, y: 0.5 });
  const mouseInfluenceRef = useRef(0);
  const uniformsRef = useRef(null);
  const colorTransitionRef = useRef({
    isTransitioning: false,
    startTime: 0,
    startColor: null,
    targetColor: null,
    duration: 500, // 0.5 seconds
    currentColor: null // Track current color state
  });
  
  // Color calculation utilities to match orb colors
  const calculateAverageOrbColors = () => {
    // Orb base colors (RGB values from 0-1)
    const baseColor1 = [0.611765, 0.262745, 0.996078]; // Purple
    const baseColor2 = [0.298039, 0.760784, 0.913725]; // Blue
    const baseColor3 = [0.062745, 0.078431, 0.600000]; // Dark blue
    
    // Calculate average RGB for default state (hue = 0)
    const defaultR = (baseColor1[0] + baseColor2[0] + baseColor3[0]) / 3;
    const defaultG = (baseColor1[1] + baseColor2[1] + baseColor3[1]) / 3;
    const defaultB = (baseColor1[2] + baseColor2[2] + baseColor3[2]) / 3;
    const defaultColor = `#${Math.round(defaultR * 255).toString(16).padStart(2, '0')}${Math.round(defaultG * 255).toString(16).padStart(2, '0')}${Math.round(defaultB * 255).toString(16).padStart(2, '0')}`;
    
    // Convert RGB to HSV, adjust hue by 253 degrees, convert back to RGB for typing state
    const rgbToHsv = (r, g, b) => {
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;
      
      let h = 0;
      if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
      }
      h = (h * 60 + 360) % 360;
      
      const s = max === 0 ? 0 : delta / max;
      const v = max;
      
      return [h, s, v];
    };
    
    const hsvToRgb = (h, s, v) => {
      const c = v * s;
      const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
      const m = v - c;
      
      let r = 0, g = 0, b = 0;
      if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
      else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
      else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
      else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
      else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
      else if (h >= 300 && h < 360) { r = c; g = 0; b = x; }
      
      return [r + m, g + m, b + m];
    };
    
    // Calculate orangish color for typing state (around 30-40 degrees for orange)
    const avgBaseHsv = rgbToHsv(defaultR, defaultG, defaultB);
    const orangeHue = 35; // Nice orange hue
    const typingRgb = hsvToRgb(orangeHue, avgBaseHsv[1] * 0.8, avgBaseHsv[2] * 1.1); // Slightly less saturated, slightly brighter
    const typingColor = `#${Math.round(typingRgb[0] * 255).toString(16).padStart(2, '0')}${Math.round(typingRgb[1] * 255).toString(16).padStart(2, '0')}${Math.round(typingRgb[2] * 255).toString(16).padStart(2, '0')}`;
    
    return {
      default: defaultColor,
      typing: typingColor
    };
  };
  
  // Color interpolation utilities
  const hexToRgb = hex => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255]
      : [1, 1, 1];
  };

  const rgbToHex = (r, g, b) => {
    const toHex = (c) => Math.round(c * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const lerp = (start, end, factor) => start + (end - start) * factor;

  const lerpColor = (startColor, endColor, factor) => {
    const start = hexToRgb(startColor);
    const end = hexToRgb(endColor);
    const r = lerp(start[0], end[0], factor);
    const g = lerp(start[1], end[1], factor);
    const b = lerp(start[2], end[2], factor);
    return rgbToHex(r, g, b);
  };

  // Get current color based on typing state with smooth transitions
  const getCurrentColor = () => {
    const orbColors = calculateAverageOrbColors();
    
    // Initialize current color if not set
    if (colorTransitionRef.current.currentColor === null) {
      colorTransitionRef.current.currentColor = isTyping ? orbColors.typing : orbColors.default;
      return colorTransitionRef.current.currentColor;
    }
    
    if (colorTransitionRef.current.isTransitioning) {
      const elapsed = performance.now() - colorTransitionRef.current.startTime;
      const progress = Math.min(elapsed / colorTransitionRef.current.duration, 1);
      
      // Smooth easing function (ease-in-out)
      const easedProgress = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const interpolatedColor = lerpColor(
        colorTransitionRef.current.startColor,
        colorTransitionRef.current.targetColor,
        easedProgress
      );
      
      if (progress >= 1) {
        colorTransitionRef.current.isTransitioning = false;
        colorTransitionRef.current.currentColor = colorTransitionRef.current.targetColor;
      }
      
      return interpolatedColor;
    }
    
    return colorTransitionRef.current.currentColor;
  };

  // Start color transition
  const startColorTransition = (newColor) => {
    const currentColor = getCurrentColor();
    colorTransitionRef.current = {
      ...colorTransitionRef.current,
      isTransitioning: true,
      startTime: performance.now(),
      startColor: currentColor,
      targetColor: newColor,
      duration: 500
    };
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      alpha: true,
      antialias: false,
      premultipliedAlpha: false
    });
    const gl = renderer.gl;
    gl.clearColor(0.0, 0.0, 0.0, 1.0); // Set clear color to black
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.canvas.style.width = '100%';
    gl.canvas.style.height = '100%';
    gl.canvas.style.backgroundColor = 'black'; // Ensure canvas background is black
    containerRef.current.appendChild(gl.canvas);

    const vert = `
attribute vec2 position;
varying vec2 vUv;
void main() {
    vUv = position * 0.5 + 0.5;
    gl_Position = vec4(position, 0.0, 1.0);
}`;

    const frag = `precision highp float;
uniform float iTime;
uniform vec2 iResolution;
uniform bool enableRainbow;
uniform vec3 gridColor;
uniform float rippleIntensity;
uniform float rippleSpeed;
uniform float gridSize;
uniform float gridThickness;
uniform float fadeDistance;
uniform float vignetteStrength;
uniform float glowIntensity;
uniform float opacity;
uniform float gridRotation;
uniform bool mouseInteraction;
uniform vec2 mousePosition;
uniform float mouseInfluence;
uniform float mouseInteractionRadius;
varying vec2 vUv;

float pi = 3.141592;

mat2 rotate(float angle) {
    float s = sin(angle);
    float c = cos(angle);
    return mat2(c, -s, s, c);
}

void main() {
    vec2 uv = vUv * 2.0 - 1.0;
    uv.x *= iResolution.x / iResolution.y;

    if (gridRotation != 0.0) {
        uv = rotate(gridRotation * pi / 180.0) * uv;
    }

    float dist = length(uv);
    float func = sin(pi * (iTime * rippleSpeed - dist));
    vec2 rippleUv = uv + uv * func * rippleIntensity;

    if (mouseInteraction && mouseInfluence > 0.0) {
        vec2 mouseUv = (mousePosition * 2.0 - 1.0);
        mouseUv.x *= iResolution.x / iResolution.y;
        float mouseDist = length(uv - mouseUv);
        
        float influence = mouseInfluence * exp(-mouseDist * mouseDist / (mouseInteractionRadius * mouseInteractionRadius));
        
        float mouseWave = sin(pi * (iTime * rippleSpeed * 2.0 - mouseDist * 3.0)) * influence;
        rippleUv += normalize(uv - mouseUv) * mouseWave * rippleIntensity * 0.3;
    }

    vec2 a = sin(gridSize * 0.5 * pi * rippleUv - pi / 2.0);
    vec2 b = abs(a);

    float aaWidth = 0.5;
    vec2 smoothB = vec2(
        smoothstep(0.0, aaWidth, b.x),
        smoothstep(0.0, aaWidth, b.y)
    );

    vec3 color = vec3(0.0);
    color += exp(-gridThickness * smoothB.x * (0.8 + 0.5 * sin(pi * iTime * rippleSpeed)));
    color += exp(-gridThickness * smoothB.y);
    color += 0.5 * exp(-(gridThickness / 4.0) * sin(smoothB.x));
    color += 0.5 * exp(-(gridThickness / 3.0) * smoothB.y);

    if (glowIntensity > 0.0) {
        color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.x);
        color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.y);
    }

    float ddd = exp(-2.0 * clamp(pow(dist, fadeDistance), 0.0, 1.0));
    
    vec2 vignetteCoords = vUv - 0.5;
    float vignetteDistance = length(vignetteCoords);
    float vignette = 1.0 - pow(vignetteDistance * 2.0, vignetteStrength);
    vignette = clamp(vignette, 0.0, 1.0);
    
    vec3 t;
    if (enableRainbow) {
        t = vec3(
            uv.x * 0.5 + 0.5 * sin(iTime * rippleSpeed),
            uv.y * 0.5 + 0.5 * cos(iTime * rippleSpeed),
            pow(cos(iTime * rippleSpeed), 4.0)
        ) + 0.5;
    } else {
        t = gridColor;
    }

    float finalFade = ddd * vignette;
    float alpha = length(color) * finalFade * opacity;
    gl_FragColor = vec4(color * t * finalFade * opacity, alpha);
}`;

    const uniforms = {
      iTime: { value: 0 },
      iResolution: { value: [1, 1] },
      enableRainbow: { value: enableRainbow },
      gridColor: { value: hexToRgb(getCurrentColor()) },
      rippleIntensity: { value: rippleIntensity },
      rippleSpeed: { value: rippleSpeed },
      gridSize: { value: gridSize },
      gridThickness: { value: gridThickness },
      fadeDistance: { value: fadeDistance },
      vignetteStrength: { value: vignetteStrength },
      glowIntensity: { value: glowIntensity },
      opacity: { value: opacity },
      gridRotation: { value: gridRotation },
      mouseInteraction: { value: mouseInteraction },
      mousePosition: { value: [0.5, 0.5] },
      mouseInfluence: { value: 0 },
      mouseInteractionRadius: { value: mouseInteractionRadius }
    };

    uniformsRef.current = uniforms;

    const geometry = new Triangle(gl);
    const program = new Program(gl, { vertex: vert, fragment: frag, uniforms });
    const mesh = new Mesh(gl, { geometry, program });

    const resize = () => {
      const { clientWidth: w, clientHeight: h } = containerRef.current;
      renderer.setSize(w, h);
      uniforms.iResolution.value = [w, h];
    };

    const handleMouseMove = e => {
      if (!mouseInteraction || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height; // Flip Y coordinate
      targetMouseRef.current = { x, y };
    };

    const handleMouseEnter = () => {
      if (!mouseInteraction) return;
      mouseInfluenceRef.current = 1.0;
    };

    const handleMouseLeave = () => {
      if (!mouseInteraction) return;
      mouseInfluenceRef.current = 0.0;
    };

    window.addEventListener('resize', resize);
    if (mouseInteraction) {
      containerRef.current.addEventListener('mousemove', handleMouseMove);
      containerRef.current.addEventListener('mouseenter', handleMouseEnter);
      containerRef.current.addEventListener('mouseleave', handleMouseLeave);
    }
    resize();

    const render = t => {
      uniforms.iTime.value = t * 0.001;

      const lerpFactor = 0.1;
      mousePositionRef.current.x += (targetMouseRef.current.x - mousePositionRef.current.x) * lerpFactor;
      mousePositionRef.current.y += (targetMouseRef.current.y - mousePositionRef.current.y) * lerpFactor;

      const currentInfluence = uniforms.mouseInfluence.value;
      const targetInfluence = mouseInfluenceRef.current;
      uniforms.mouseInfluence.value += (targetInfluence - currentInfluence) * 0.05;

      uniforms.mousePosition.value = [mousePositionRef.current.x, mousePositionRef.current.y];

      // Update color during transitions
      if (colorTransitionRef.current.isTransitioning) {
        const currentColor = getCurrentColor();
        uniforms.gridColor.value = hexToRgb(currentColor);
      }

      renderer.render({ scene: mesh });
      requestAnimationFrame(render);
    };

    requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      if (mouseInteraction && containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
        containerRef.current.removeEventListener('mouseenter', handleMouseEnter);
        containerRef.current.removeEventListener('mouseleave', handleMouseLeave);
      }
      renderer.gl.getExtension('WEBGL_lose_context')?.loseContext();
      containerRef.current?.removeChild(gl.canvas);
    };
  }, []);

  // Handle typing state changes with smooth transitions
  useEffect(() => {
    if (!uniformsRef.current) return;

    const orbColors = calculateAverageOrbColors();
    const targetColor = isTyping ? orbColors.typing : orbColors.default;
    console.log('Starting color transition:', { isTyping, targetColor });
    startColorTransition(targetColor);
  }, [isTyping]);

  useEffect(() => {
    if (!uniformsRef.current) return;

    uniformsRef.current.enableRainbow.value = enableRainbow;
    // Don't update gridColor here - let the transition system handle it
    uniformsRef.current.rippleIntensity.value = rippleIntensity;
    uniformsRef.current.rippleSpeed.value = rippleSpeed;
    uniformsRef.current.gridSize.value = gridSize;
    uniformsRef.current.gridThickness.value = gridThickness;
    uniformsRef.current.fadeDistance.value = fadeDistance;
    uniformsRef.current.vignetteStrength.value = vignetteStrength;
    uniformsRef.current.glowIntensity.value = glowIntensity;
    uniformsRef.current.opacity.value = opacity;
    uniformsRef.current.gridRotation.value = gridRotation;
    uniformsRef.current.mouseInteraction.value = mouseInteraction;
    uniformsRef.current.mouseInteractionRadius.value = mouseInteractionRadius;
  }, [
    enableRainbow,
    gridColor,
    rippleIntensity,
    rippleSpeed,
    gridSize,
    gridThickness,
    fadeDistance,
    vignetteStrength,
    glowIntensity,
    opacity,
    gridRotation,
    mouseInteraction,
    mouseInteractionRadius
    // Removed isTyping from dependency array since we handle color transitions separately
  ]);

  return <div ref={containerRef} className="ripple-grid-container" />;
};

export default RippleGrid;
